import React from 'react';
import { ScanSearch, Plus, CheckCircle2, ArrowRight, ChevronRight } from 'lucide-react';
import { ApplicationResponse } from '../../types/application';
import { Finding } from '../../types/finding';
import { ScanBatch } from '../../types/scan';
import { getApplicationDisplayTitle } from '../../utils/repository';
import { getFindingTitle } from '../../utils/findingUtils';
import { Button, FindingCard, Spinner } from '../../design-system';
import { getGreeting } from './greeting';
import { OverviewStats } from './types';
import TrialBanner from './TrialBanner';

export interface ScanProgressInfo {
  active: boolean;
  percent: number;
  findingsCount: number;
  currentOperation: string;
}

export interface RepositoryOverviewProps {
  application: ApplicationResponse | null;
  stats: OverviewStats;
  recentScans: ScanBatch[];
  recentFindings: Finding[];
  isLoading: boolean;
  firstName: string;
  onRunScan: () => void;
  onViewAllFindings: () => void;
  onOpenFinding: (findingId: string) => void;
  /** Live progress for an in-flight scan, used to render the running view. */
  scanProgress?: ScanProgressInfo;
  /** Optimistically show the running view on arrival (e.g. right after onboarding). */
  expectFirstScan?: boolean;
}

const HOW_IT_WORKS_STEPS = [
  'Scanning the repo',
  'Review real findings',
  'One-click AI fix',
  'PR merges, auto-resolved',
];

const relativeFromNow = (iso?: string): string | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
};

const CARD_BASE: React.CSSProperties = {
  background: 'var(--surface-1)',
  borderRadius: 'var(--radius-2xl)',
};

const RepositoryOverview: React.FC<RepositoryOverviewProps> = ({
  application,
  stats,
  recentScans,
  recentFindings,
  isLoading,
  firstName,
  onRunScan,
  onViewAllFindings,
  onOpenFinding,
  scanProgress,
  expectFirstScan = false,
}) => {
  const repoName = application ? getApplicationDisplayTitle(application) : 'this repository';
  const openCount = stats.findings.byStatus.open + stats.findings.byStatus.needsReview;
  const scansTotal = stats.scans.total;
  const firstRun = scansTotal === 0;
  const scanning = stats.scans.running > 0 || !!scanProgress?.active || (firstRun && expectFirstScan);
  const unresolved = recentFindings.filter(
    (f) => f.status === 'open' || f.status === 'needs_manual_review',
  );

  type OverviewState = 'firstRun' | 'scanning' | 'backlog' | 'allClear';
  const state: OverviewState = firstRun
    ? scanning
      ? 'scanning'
      : 'firstRun'
    : openCount > 0
    ? 'backlog'
    : scanning
    ? 'scanning'
    : 'allClear';

  const lastScanAt = recentScans[0]?.completed_at || recentScans[0]?.created_at;
  const lastScanRel = relativeFromNow(lastScanAt);

  const container = (children: React.ReactNode) => (
    <div
      style={{
        minHeight: '100%',
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div style={{ maxWidth: 'var(--content-max)', margin: '0 auto', padding: '32px 40px 80px' }}>
        {children}
      </div>
    </div>
  );

  if (isLoading) {
    return container(
      <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
        <Spinner size={28} />
      </div>,
    );
  }

  return container(
    <>
      {/* Greeting */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            fontSize: 27,
            fontWeight: 700,
            letterSpacing: 'var(--tracking-display)',
            margin: '0 0 6px',
            color: 'var(--text-primary)',
          }}
        >
          {getGreeting()}, {firstName}
        </h1>
        <p style={{ margin: 0, fontSize: 14.5, color: 'var(--text-secondary)' }}>
          {state === 'firstRun' ? (
            <>
              Let's get <strong style={{ color: 'var(--text-strong)' }}>{repoName}</strong> scanned for real
              vulnerabilities.
            </>
          ) : state === 'scanning' ? (
            <>
              Scanning <strong style={{ color: 'var(--text-strong)' }}>{repoName}</strong> — findings will appear
              shortly.
            </>
          ) : state === 'backlog' ? (
            <>
              Here's the backlog that needs your attention in{' '}
              <strong style={{ color: 'var(--text-strong)' }}>{repoName}</strong>.
            </>
          ) : (
            <>
              <strong style={{ color: 'var(--text-strong)' }}>{repoName}</strong> is looking healthy — no unresolved
              findings.
            </>
          )}
        </p>
      </div>

      <TrialBanner />

      {/* First-run CTA */}
      {state === 'firstRun' && (
        <div style={{ ...CARD_BASE, border: '1px solid var(--lime-soft-border)', padding: '34px 32px' }}>
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: 'var(--radius-md)',
              background: 'var(--lime-soft)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 18,
              color: 'var(--accent-ink)',
            }}
          >
            <ScanSearch style={{ width: 24, height: 24 }} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 8px', color: 'var(--text-primary)' }}>
            Run your first security scan
          </h2>
          <p style={{ margin: '0 0 22px', fontSize: 14.5, lineHeight: 1.6, color: 'var(--text-secondary)', maxWidth: 560 }}>
            Kolega performs a deep, cross-file AI security analysis of {repoName} and surfaces real, actionable
            vulnerabilities — not noise. Findings appear here the moment they're ready.
          </p>
          <Button size="lg" icon={<ScanSearch style={{ width: 17, height: 17 }} />} onClick={onRunScan}>
            Run Deep AI Security Scan
          </Button>
        </div>
      )}

      {/* Scanning */}
      {state === 'scanning' && (() => {
        const percent = Math.max(0, Math.min(100, Math.round(scanProgress?.percent ?? 0)));
        const phaseLabel = scanProgress?.currentOperation || 'Starting scan…';
        const found = scanProgress?.findingsCount ?? 0;
        const foundLabel =
          found === 0 ? 'No findings yet' : `${found} potential ${found === 1 ? 'finding' : 'findings'} so far`;
        const branch = application?.repositories?.[0]?.default_branch || 'main';

        return (
          <div style={{ ...CARD_BASE, border: '1px solid var(--lime-soft-border)', padding: '30px 32px' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <Spinner size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px', color: 'var(--text-primary)' }}>
                  {firstRun ? 'Your first scan is already running' : <>Scanning {repoName}…</>}
                </h2>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--text-secondary)' }}>
                  {firstRun ? (
                    <>
                      We kicked off a Deep AI Security Scan of{' '}
                      <strong style={{ color: 'var(--text-strong)' }}>{repoName}</strong> as soon as you connected it.
                      Findings appear here automatically — no need to do anything.
                    </>
                  ) : (
                    <>Your findings will populate here automatically. Feel free to keep working.</>
                  )}
                </p>
              </div>
            </div>

            {/* Live progress */}
            <div
              style={{
                background: 'var(--bg-sunken)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-lg)',
                padding: '18px 20px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--accent-ink)' }}>{phaseLabel}</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--accent-ink)', fontFamily: 'var(--font-mono)' }}>
                  {percent}%
                </span>
              </div>
              <div style={{ height: 7, borderRadius: 5, background: 'var(--surface-3)', overflow: 'hidden', marginBottom: 12 }}>
                <div
                  style={{
                    height: '100%',
                    width: `${percent}%`,
                    background: 'linear-gradient(90deg, var(--lime-deep), var(--lime))',
                    borderRadius: 5,
                    transition: 'width .5s ease',
                  }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 18,
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-mono)',
                  flexWrap: 'wrap',
                }}
              >
                <span>
                  {repoName} · {branch}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>{foundLabel}</span>
                <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>Runs in the background</span>
              </div>
            </div>

            {/* How it works (first scan only) */}
            {firstRun && (
              <>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.07em',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    margin: '24px 0 11px',
                  }}
                >
                  How Kolega DevSec works
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  {HOW_IT_WORKS_STEPS.map((label, i) => (
                    <React.Fragment key={label}>
                      {i > 0 && <ChevronRight style={{ width: 14, height: 14, color: 'var(--text-faint)' }} />}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          background: 'var(--bg-sunken)',
                          border: `1px solid ${i === 0 ? 'var(--lime-soft-border)' : 'var(--border-subtle)'}`,
                          borderRadius: 'var(--radius-md)',
                          padding: '11px 14px',
                          fontSize: 12.5,
                          color: i === 0 ? 'var(--text-strong)' : 'var(--text-muted)',
                        }}
                      >
                        <span style={{ color: 'var(--accent-ink)', fontWeight: 800 }}>{i + 1}</span> {label}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Backlog */}
      {state === 'backlog' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
              }}
            >
              {openCount} open {openCount === 1 ? 'finding' : 'findings'} · work top-down
            </div>
            <button
              onClick={onViewAllFindings}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--accent-ink)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              View all findings <ArrowRight style={{ width: 14, height: 14 }} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {unresolved.map((f) => (
              <FindingCard
                key={f.id}
                compact
                title={getFindingTitle(f) || f.message}
                severity={f.severity}
                status={f.status}
                cvss={f.cvss_base_score != null ? f.cvss_base_score.toFixed(1) : undefined}
                file={f.file_path}
                hasPR={!!f.fix_summary?.has_pr}
                onClick={() => onOpenFinding(f.id)}
              />
            ))}
          </div>
        </>
      )}

      {/* All clear */}
      {state === 'allClear' && (
        <div
          style={{
            ...CARD_BASE,
            border: '1px solid rgba(74,222,128,0.22)',
            padding: '38px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 'var(--radius-xl)',
              background: 'var(--status-resolved-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
              color: 'var(--status-resolved)',
            }}
          >
            <CheckCircle2 style={{ width: 26, height: 26 }} />
          </div>
          <h2 style={{ fontSize: 21, fontWeight: 700, margin: '0 0 8px', color: 'var(--text-primary)' }}>
            You're all clear
          </h2>
          <p style={{ margin: '0 0 22px', fontSize: 14, color: 'var(--text-secondary)', maxWidth: 440, lineHeight: 1.6 }}>
            No unresolved findings in {repoName}.
            {lastScanRel ? (
              <>
                {' '}
                Your last scan was <strong style={{ color: 'var(--text-strong)' }}>{lastScanRel}</strong> — run a fresh
                scan to catch anything new.
              </>
            ) : (
              ' Run a fresh scan to catch anything new.'
            )}
          </p>
          <Button icon={<Plus style={{ width: 16, height: 16 }} />} onClick={onRunScan}>
            Run a new scan
          </Button>
        </div>
      )}
    </>,
  );
};

export default RepositoryOverview;
